/**
 * The SOUL MERIDIAN design language, shared by every social surface.
 *
 * The title screen is the game's one piece of authored graphic design: a white
 * spine that frays into six coloured strands over near-black, thin light type
 * in wide uppercase tracking, and controls that are text — underlines and
 * glows, never boxes. Chat, friends, parties and guilds are the screens a
 * player lives in AFTER that first screen, so they speak the same language:
 *
 *  - the six strand hues become the social palette (one per relationship),
 *  - every panel wears the meridian line as its header rule (`MeridianLine` in
 *    a strip, stirred whenever the view changes — the title's own gesture),
 *  - type is thin Helvetica Neue, uppercase + tracked for labels, lowercase for
 *    input and actions,
 *  - refusals use the title's auth-error red, never a new one.
 *
 * The classes themselves live in style.css under "Social — the meridian
 * language"; this module owns the colours (three.js and canvas need literals)
 * and the header-rule helper.
 */
import { MeridianLine } from "./meridianLine";

/** The title line's six base hues (meridianLine.ts BASE_HUES), one per strand.
 *  Assigning them by relationship is what makes the palette *mean* something:
 *  a whisper is always violet, a party always green, a guild always amethyst. */
export const MERIDIAN_HUES = {
  /** The room you're standing in — the common air. */
  local: 188,
  /** Friends: presence, requests, the people you chose. */
  friends: 206,
  /** Whispers — one thread, off to the side. */
  whisper: 258,
  /** The party you move with. */
  party: 166,
  /** The guild you belong to (also the over-head badge). */
  guild: 282,
  /** You, and the server speaking as itself. */
  self: 222,
} as const;

export type Strand = keyof typeof MERIDIAN_HUES;

/** A strand colour. Defaults match the title line's crisp core (62% / 82%);
 *  pass a lower lightness for rules and a higher saturation for glows. */
export function strand(name: Strand, lit = 82, sat = 62): string {
  return `hsl(${MERIDIAN_HUES[name]} ${sat}% ${lit}%)`;
}

/** Body copy over the near-black — the title's white, one step softened. */
export const MERIDIAN_INK = "#eef4ff";
/** Secondary copy (systems, timestamps, hints). */
export const MERIDIAN_DIM = "rgba(255, 255, 255, 0.45)";
/** Refusals + destructive arming. The title's own `#auth-error` red. */
export const MERIDIAN_WARN = "#ff9a8a";

/**
 * Mount the line motif as a panel's header rule: a short spine on the left
 * that frays into the six strands on the right, drifting forever. `stir()` on
 * every view change is the gesture the title screen already makes.
 *
 * The caller owns start/stop — an animating rule behind a closed panel is
 * wasted frames.
 */
export function mountMeridianRule(host: HTMLElement, splitFrac = 0.4): MeridianLine {
  // A header rule is ~30px tall where the title screen is ~1000: the fan has to
  // open much wider (as a fraction of height) or the six strands overlap into
  // a single bright band instead of reading as a fray.
  const line = new MeridianLine({ splitFrac, spreadFrac: 0.5 });
  line.setMode("title");
  line.el.classList.add("mrd-rule-line");
  host.appendChild(line.el);
  return line;
}
